import { AlertTriangle } from "lucide-react";
import { SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs, Container } from "@/components/site/Primitives";

const DraftBanner = () => (
  <div role="note" className="flex items-start gap-3 rounded-[14px] border border-black/20 bg-[#F7F7F7] p-5 text-sm leading-6" data-testid="terms-draft-banner">
    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
    <p><strong>Draft for owner review.</strong> This document has been prepared as a starting point and has not yet been approved by {SITE.legalName}. It is not legal advice and may change before it takes effect.</p>
  </div>
);

const LegalLayout = ({ title, path, description, updated, children }) => (
  <>
    <Seo title={`${title} | ${SITE.legalName}`} description={description} path={path} noindex />
    <section className="bg-white pb-20">
      <Container className="pt-6"><Breadcrumbs items={[{ name: title, path }]} /></Container>
      <Container className="max-w-3xl pt-10">
        <h1 className="kl-h1">{title}</h1>
        <p className="mt-4 text-sm text-black/55">Draft prepared: {updated}</p>
        <div className="mt-8"><DraftBanner /></div>
        <div className="kl-prose mt-10">{children}</div>
      </Container>
    </section>
  </>
);

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" path="/privacy-policy" updated="2026" description="How KhozaLand (Pty) Ltd collects, uses and protects personal information in line with POPIA.">
      <h2>1. Who we are</h2>
      <p>{SITE.legalName} (registration number {SITE.registrationNumber}) is the responsible party for personal information collected through this website. We are based in {SITE.locationLabel}, South Africa. Contact: <a href={`mailto:${SITE.email}`}>{SITE.email}</a> · {SITE.phoneDisplay}.</p>
      <h2>2. Information we collect</h2>
      <p>When you request a quotation, a bulk order quote or our product catalogue, we collect the information you choose to provide, such as your name, surname, email address, phone/WhatsApp number, company or organisation name, product requirements, quantities, sizes, colours, design requirements, required date, delivery location, approximate budget and any reference file you upload.</p>
      <p>We also record the page you submitted from and the date of submission. We do not ask for identity numbers or payment card details on this website.</p>
      <h2>3. Why we use it</h2>
      <ul>
        <li>To respond to your enquiry and prepare a quotation.</li>
        <li>To communicate with you about your order using your preferred contact method.</li>
        <li>To keep basic records of enquiries and quotations.</li>
        <li>To send you our catalogue, if you asked for it.</li>
      </ul>
      <p>We process your information on the basis of your consent and to take steps you have requested before entering into an agreement with us.</p>
      <h2>4. Reference files and designs</h2>
      <p>Uploaded files are stored privately, are not publicly accessible and are only used to prepare your quotation and order. Please only upload logos, photographs and designs you have the right to use.</p>
      <h2>5. Sharing</h2>
      <p>We do not sell your personal information. We may share limited information with service providers who help us operate (for example website hosting, production partners or couriers such as PAXI) only where necessary to deliver your order, and they must protect it.</p>
      <h2>6. Analytics and cookies</h2>
      <p>Optional analytics cookies are only used if you accept them. We also keep anonymous counts of button clicks (for example WhatsApp clicks) that do not identify you.</p>
      <h2>7. Security and retention</h2>
      <p>We use reasonable technical and organisational measures to protect your information, including secure connections and restricted access. We keep enquiry records only for as long as needed for the purposes above or as required by law.</p>
      <h2>8. Your rights</h2>
      <p>Under the Protection of Personal Information Act (POPIA) you may ask to access, correct or delete your personal information, object to processing or withdraw consent. Contact us at <a href={`mailto:${SITE.email}`}>{SITE.email}</a>. You may also lodge a complaint with the Information Regulator (South Africa).</p>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms & Conditions" path="/terms" updated="2026" description="Draft terms for quotations and custom orders from KhozaLand (Pty) Ltd.">
      <h2>1. Website information</h2>
      <p>This website provides information about {SITE.legalName}'s products and services and lets you request quotations. No payments or orders are processed online.</p>
      <h2>2. Quotations</h2>
      <p>Prices shown on this website are indicative “from” prices from our current price list. Your final price depends on product, quantity, design and finish and is stated in your quotation. Submitting an enquiry does not create an order.</p>
      <h2>3. Order confirmation</h2>
      <p>An order is only confirmed once you have approved the quotation and KhozaLand has confirmed the order. Deposit requirements and payment terms are stated in your quotation.</p>
      <h2>4. Your designs, logos and wording</h2>
      <p>You confirm that you have the right to use any logos, images, names and designs you supply. Please check all wording carefully — including names, dates, clan names and iziduko — as we produce items according to the details you approve.</p>
      <h2>5. Production times</h2>
      <p>Production times depend on the product and order and are confirmed in your quotation. Urgent orders are not accepted.</p>
      <h2>6. Delivery and collection</h2>
      <p>Delivery or courier options, including PAXI for suitable orders outside Cape Town, are subject to confirmation for each order.</p>
      <h2>7. Cancellations, returns and refunds</h2>
      <p>Terms for cancellations, returns and refunds will be confirmed by KhozaLand and stated in your quotation. Nothing in these terms limits your rights under the Consumer Protection Act.</p>
      <h2>8. Contact</h2>
      <p>Questions about these terms: <a href={`mailto:${SITE.email}`}>{SITE.email}</a> · {SITE.phoneDisplay}.</p>
    </LegalLayout>
  );
}
